import { useMemo, useState } from "react";
import { Copy, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import LicenseCommercialLegalSection from "@/components/LicenseCommercialLegalSection";
import LicenseInvoiceSection from "@/components/LicenseInvoiceSection";
import LicensePaymentProofSection from "@/components/LicensePaymentProofSection";
import LicenseSubmissionGate from "@/components/LicenseSubmissionGate";
import { useCommercialLegalAcceptance } from "@/lib/useCommercialLegalAcceptance";
import {
  buildLicenseRequestFormDefaults,
  isSubmissionBlocked,
  LICENSE_REQUEST_MSG_SUCCESS,
  LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED,
  moduleLabel,
  parseLicenseRequestSubmitError,
  resolveRequestableModuleIds,
  validateInvoiceFields,
} from "@/lib/commercialLicenseRequests";
import { createLicenseRequest } from "@/lib/commercialLicenseRequestsApi";
import {
  buildExpansionRequestPayload,
  buildWhatsAppExpansionMessage,
  expansionModulesForLegalCheck,
  EXPANSION_KIND_CLIENT_LIMIT,
  EXPANSION_KIND_MIXED,
  EXPANSION_KIND_MODULE,
  EXPANSION_KIND_PLAN_UPGRADE,
  EXPANSION_KIND_RENEWAL,
  EXPANSION_KIND_SPECIAL,
  EXPANSION_KIND_USER_LIMIT,
  BILLING_CADENCE_OPTIONS,
  expansionTypeOptionsForContext,
  licenseTypeFromBillingCadence,
  readLicenseExpansionContext,
} from "@/lib/commercialLicenseExpansion";
import { billingCadenceLabel } from "@/lib/commercialPlanUi";
import { requestedLicenseTypeOptions } from "@/lib/licenseTypes";
import { formatLicenseDateForDisplay } from "@/lib/licenseDates";
import { toast } from "sonner";

function StatRow({ label, value }) {
  return (
    <div className="flex justify-between gap-2 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right">{value ?? "—"}</span>
    </div>
  );
}

/**
 * Formulario corto de ampliación/renovación (licencia activa).
 */
export default function LicenseExpansionForm({
  systemInfo,
  profileDefaults = null,
  gate,
  onGateChange,
  onSubmitted,
  user = null,
  initialExpansionType = null,
  preselectedModules = null,
  previewReadOnly = false,
}) {
  const profile = profileDefaults || systemInfo?.profile || {};
  const ctx = useMemo(() => readLicenseExpansionContext(systemInfo), [systemInfo]);
  const [form, setForm] = useState(() => {
    const defaults = buildLicenseRequestFormDefaults(systemInfo, profile);
    const requested_modules = { ...defaults.requested_modules };
    if (preselectedModules && typeof preselectedModules === "object") {
      Object.entries(preselectedModules).forEach(([id, on]) => {
        if (on) requested_modules[id] = true;
      });
    }
    return {
      ...defaults,
      expansion_type: initialExpansionType || EXPANSION_KIND_USER_LIMIT,
      commercial_plan: ctx.commercialPlan || defaults.commercial_plan,
      billing_cadence: ctx.billingCadence || defaults.billing_cadence || "annual",
      requested_authorized_users: "",
      requested_authorized_clients: "",
      requested_modules,
    };
  });
  const expansionTypeOptions = useMemo(
    () => expansionTypeOptionsForContext(systemInfo),
    [systemInfo],
  );
  const [submitting, setSubmitting] = useState(false);
  const requestableIds = resolveRequestableModuleIds(systemInfo);
  const blocked = isSubmissionBlocked(gate);
  const readOnly = Boolean(previewReadOnly);
  const expansionModulesForLegal = useMemo(
    () => expansionModulesForLegalCheck(form, requestableIds),
    [form, requestableIds],
  );
  const expansionLegal = useCommercialLegalAcceptance(
    true,
    { form, user, systemInfo },
    { expansionMode: true, expansionModulesForLegal },
  );

  const whatsappText = useMemo(
    () => buildWhatsAppExpansionMessage(ctx, form),
    [ctx, form],
  );

  const copyWhatsApp = async () => {
    try {
      await navigator.clipboard.writeText(whatsappText);
      toast.success("Mensaje copiado. La solicitud debe enviarse también por el botón «Enviar solicitud».");
    } catch {
      toast.error("No se pudo copiar al portapapeles.");
    }
  };

  const validate = () => {
    const kind = form.expansion_type;
    if (kind === EXPANSION_KIND_USER_LIMIT) {
      const n = Number(form.requested_authorized_users);
      if (!n || n <= Number(ctx.authorizedUsers || 0)) {
        return "Indique un nuevo límite de usuarios mayor al actual.";
      }
    }
    if (kind === EXPANSION_KIND_CLIENT_LIMIT) {
      const n = Number(form.requested_authorized_clients);
      if (!n || n <= Number(ctx.authorizedClients || 0)) {
        return "Indique un nuevo límite de clientes mayor al actual.";
      }
    }
    if (kind === EXPANSION_KIND_MODULE) {
      const any = requestableIds.some((id) => form.requested_modules?.[id]);
      if (!any) return "Seleccione al menos un módulo adicional.";
    }
    if (kind === EXPANSION_KIND_RENEWAL && !ctx.usesPlanModel && !form.requested_license_type) {
      return "Seleccione el tipo de renovación solicitada.";
    }
    if (kind === EXPANSION_KIND_RENEWAL && ctx.usesPlanModel && !form.billing_cadence) {
      return "Seleccione la cadencia de renovación.";
    }
    if (kind === EXPANSION_KIND_PLAN_UPGRADE && !form.billing_cadence) {
      return "Seleccione la cadencia de cobro para el plan Profesional.";
    }
    if (kind === EXPANSION_KIND_SPECIAL && !String(form.comments || "").trim()) {
      return "Describa el ajuste especial solicitado.";
    }
    if (kind === EXPANSION_KIND_MIXED) {
      const hasU = form.requested_authorized_users
        && Number(form.requested_authorized_users) > Number(ctx.authorizedUsers || 0);
      const hasC = form.requested_authorized_clients
        && Number(form.requested_authorized_clients) > Number(ctx.authorizedClients || 0);
      const hasM = requestableIds.some((id) => form.requested_modules?.[id]);
      const hasRenewal = Boolean(form.requested_license_type);
      const hasComment = Boolean(String(form.comments || "").trim());
      if (!hasU && !hasC && !hasM && !hasRenewal && !hasComment) {
        return "Indique al menos un cambio en la solicitud mixta.";
      }
    }
    const invErr = validateInvoiceFields(form);
    if (invErr) return invErr;
    if (!expansionLegal.canProceedWithRequest) {
      return LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED;
    }
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    if (blocked) {
      toast.error(gate?.message || "Ya hay una solicitud en revisión.");
      return;
    }
    setSubmitting(true);
    try {
      let agreementId = expansionLegal.agreementAcceptanceId;
      if (!agreementId) {
        toast.error(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
        return;
      }
      const payload = buildExpansionRequestPayload(
        form,
        user || { email: form.email },
        agreementId,
        { requestableModuleIds: requestableIds },
      );
      await createLicenseRequest(payload);
      toast.success(LICENSE_REQUEST_MSG_SUCCESS);
      onSubmitted?.();
    } catch (e) {
      toast.error(parseLicenseRequestSubmitError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const kind = form.expansion_type;

  return (
    <div className="space-y-4" data-testid="license-expansion-form">
      <section className="rounded-md border border-slate-200 bg-slate-50/60 p-4 space-y-2">
        <h5 className="text-xs font-semibold text-[#0F2D5C] m-0">Licencia activa — referencia</h5>
        <StatRow label="Titular" value={ctx.titular} />
        <StatRow label="RFC" value={ctx.rfc} />
        {ctx.usesPlanModel ? (
          <>
            <StatRow label="Plan actual" value={ctx.commercialPlanLabel} />
            <StatRow label="Cadencia" value={billingCadenceLabel(ctx.billingCadence)} />
          </>
        ) : null}
        <StatRow
          label="Vigencia"
          value={
            ctx.validFrom && ctx.validUntil
              ? `${formatLicenseDateForDisplay(ctx.validFrom)} — ${formatLicenseDateForDisplay(ctx.validUntil)}`
              : "—"
          }
        />
        <StatRow label="Usuarios autorizados" value={ctx.authorizedUsers} />
        <StatRow label="Usuarios activos" value={ctx.activeUsers} />
        <StatRow label="Usuarios disponibles" value={ctx.availableUsers} />
        <StatRow label="Clientes autorizados" value={ctx.authorizedClients} />
        <StatRow label="Clientes activos" value={ctx.activeClients} />
        <StatRow label="Clientes disponibles" value={ctx.availableClients} />
      </section>

      <div>
        <Label className="text-xs">Tipo de solicitud</Label>
        <Select
          value={form.expansion_type}
          onValueChange={(v) => setForm((s) => ({ ...s, expansion_type: v }))}
          disabled={readOnly}
        >
          <SelectTrigger className="mt-1 h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {expansionTypeOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {kind === EXPANSION_KIND_USER_LIMIT ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-600 m-0">
            Actualmente tienes <strong>{ctx.authorizedUsers ?? "—"}</strong> usuarios autorizados.
          </p>
          <div>
            <Label className="text-xs">Nuevo límite solicitado</Label>
            <Input
              type="number"
              min={Number(ctx.authorizedUsers || 0) + 1}
              className="mt-1 h-9 text-xs"
              value={form.requested_authorized_users}
              onChange={(e) => setForm((s) => ({ ...s, requested_authorized_users: e.target.value }))}
              data-testid="expansion-requested-users"
              disabled={readOnly}
            />
          </div>
        </div>
      ) : null}

      {kind === EXPANSION_KIND_CLIENT_LIMIT ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-600 m-0">
            Actualmente tienes <strong>{ctx.authorizedClients ?? "—"}</strong> clientes autorizados.
          </p>
          <div>
            <Label className="text-xs">Nuevo límite solicitado</Label>
            <Input
              type="number"
              min={Number(ctx.authorizedClients || 0) + 1}
              className="mt-1 h-9 text-xs"
              value={form.requested_authorized_clients}
              onChange={(e) => setForm((s) => ({ ...s, requested_authorized_clients: e.target.value }))}
              data-testid="expansion-requested-clients"
              disabled={readOnly}
            />
          </div>
        </div>
      ) : null}

      {kind === EXPANSION_KIND_RENEWAL ? (
        ctx.usesPlanModel ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-600 m-0">
              Renovarás el plan <strong>{ctx.commercialPlanLabel}</strong> con la cadencia elegida. BNT confirmará vigencia y pago.
            </p>
            <div>
              <Label className="text-xs">Cadencia de renovación</Label>
              <Select
                value={form.billing_cadence || ctx.billingCadence || "annual"}
                onValueChange={(v) => setForm((s) => ({
                  ...s,
                  billing_cadence: v,
                  requested_license_type: licenseTypeFromBillingCadence(v),
                }))}
                disabled={readOnly}
              >
                <SelectTrigger className="mt-1 h-9 text-xs" data-testid="expansion-renewal-cadence">
                  <SelectValue placeholder="Seleccione…" />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_CADENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div>
            <Label className="text-xs">Tipo de renovación solicitada</Label>
            <Select
              value={form.requested_license_type || ""}
              onValueChange={(v) => setForm((s) => ({ ...s, requested_license_type: v }))}
              disabled={readOnly}
            >
              <SelectTrigger className="mt-1 h-9 text-xs">
                <SelectValue placeholder="Seleccione…" />
              </SelectTrigger>
              <SelectContent>
                {requestedLicenseTypeOptions(true).map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )
      ) : null}

      {kind === EXPANSION_KIND_PLAN_UPGRADE ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-600 m-0">
            Solicitarás el cambio de <strong>{ctx.commercialPlanLabel || "Básico"}</strong> a <strong>Profesional</strong>.
            Incluye todos los módulos y límites del plan publicado.
          </p>
          <div>
            <Label className="text-xs">Cadencia de cobro</Label>
            <Select
              value={form.billing_cadence || "annual"}
              onValueChange={(v) => setForm((s) => ({
                ...s,
                billing_cadence: v,
                requested_license_type: licenseTypeFromBillingCadence(v),
              }))}
              disabled={readOnly}
            >
              <SelectTrigger className="mt-1 h-9 text-xs" data-testid="expansion-plan-upgrade-cadence">
                <SelectValue placeholder="Seleccione…" />
              </SelectTrigger>
              <SelectContent>
                {BILLING_CADENCE_OPTIONS.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : null}

      {kind === EXPANSION_KIND_MODULE ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-600 m-0">Módulos adicionales no contratados:</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {requestableIds.map((id) => (
              <label key={id} className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox
                  checked={Boolean(form.requested_modules?.[id])}
                  onCheckedChange={(c) =>
                    setForm((s) => ({
                      ...s,
                      requested_modules: { ...s.requested_modules, [id]: Boolean(c) },
                    }))
                  }
                  disabled={readOnly}
                />
                {moduleLabel(id, ctx.catalog)}
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {kind === EXPANSION_KIND_MIXED ? (
        <div className="space-y-3 rounded-md border border-slate-200 bg-white p-3">
          <p className="text-xs text-slate-600 m-0">Indique uno o más cambios solicitados:</p>
          <div>
            <Label className="text-xs">Nuevo límite de usuarios (opcional)</Label>
            <Input
              type="number"
              min={Number(ctx.authorizedUsers || 0) + 1}
              className="mt-1 h-9 text-xs"
              value={form.requested_authorized_users}
              onChange={(e) => setForm((s) => ({ ...s, requested_authorized_users: e.target.value }))}
              disabled={readOnly}
            />
          </div>
          <div>
            <Label className="text-xs">Nuevo límite de clientes (opcional)</Label>
            <Input
              type="number"
              min={Number(ctx.authorizedClients || 0) + 1}
              className="mt-1 h-9 text-xs"
              value={form.requested_authorized_clients}
              onChange={(e) => setForm((s) => ({ ...s, requested_authorized_clients: e.target.value }))}
              disabled={readOnly}
            />
          </div>
          <div>
            <Label className="text-xs">Renovación solicitada (opcional)</Label>
            <Select
              value={form.requested_license_type || ""}
              onValueChange={(v) => setForm((s) => ({ ...s, requested_license_type: v }))}
              disabled={readOnly}
            >
              <SelectTrigger className="mt-1 h-9 text-xs">
                <SelectValue placeholder="Sin cambio de vigencia" />
              </SelectTrigger>
              <SelectContent>
                {requestedLicenseTypeOptions(true).map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {requestableIds.map((id) => (
              <label key={id} className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox
                  checked={Boolean(form.requested_modules?.[id])}
                  onCheckedChange={(c) =>
                    setForm((s) => ({
                      ...s,
                      requested_modules: { ...s.requested_modules, [id]: Boolean(c) },
                    }))
                  }
                  disabled={readOnly}
                />
                {moduleLabel(id, ctx.catalog)}
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {kind === EXPANSION_KIND_SPECIAL ? (
        <p className="text-xs text-slate-600 m-0">
          Describa en el comentario general el ajuste especial que necesita BNT revisar.
        </p>
      ) : null}

      <div>
        <Label className="text-xs">
          Comentario general{kind === EXPANSION_KIND_SPECIAL ? "" : " (opcional)"}
        </Label>
        <Input
          className="mt-1 text-xs"
          value={form.comments}
          onChange={(e) => setForm((s) => ({ ...s, comments: e.target.value }))}
          disabled={readOnly}
        />
      </div>

      {!readOnly ? (
        <>
          <LicenseInvoiceSection form={form} setForm={setForm} profile={profile} />
          <LicensePaymentProofSection form={form} setForm={setForm} disabled={blocked} />

          <LicenseCommercialLegalSection
            legalCatalog={expansionLegal.legalCatalog}
            legalLoading={expansionLegal.legalLoading}
            legalError={expansionLegal.legalError}
            legalAccepted={expansionLegal.legalAccepted}
            legalLocked={expansionLegal.legalLocked}
            legalSaving={expansionLegal.legalSaving}
            legalAcceptError={expansionLegal.legalAcceptError}
            agreementAcceptanceId={expansionLegal.agreementAcceptanceId}
            legalReused={expansionLegal.legalReused}
            reusedAcceptance={expansionLegal.reusedAcceptance}
            onLegalAcceptedChange={expansionLegal.handleLegalAcceptedChange}
            disabled={blocked}
          />

          <LicenseSubmissionGate gate={gate} onGateChange={onGateChange} />

          {gate?.expansion_pending ? (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 m-0">
              Ya tienes una solicitud de ampliación en revisión
              {gate.expansion_type_label ? ` (${gate.expansion_type_label})` : ""}.
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={copyWhatsApp}
              data-testid="expansion-copy-whatsapp"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" />
              Copiar mensaje para WhatsApp
            </Button>
            <Button
              type="button"
              size="sm"
              className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2247]"
              disabled={submitting || blocked}
              onClick={onSubmit}
              data-testid="expansion-submit"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Enviar solicitud
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
